import { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { doApiCall } from "./utils";

const { REACT_APP_REGISTRATION_API_URL } = process.env;

export interface PaymentLinkResponse {
  glMemberId: string;
  ecommerceLink: string;
}

export interface PaymentLinkApiResponse {
  data: PaymentLinkResponse;
  meta?: { message?: string; details?: string | null };
}

export type MembershipPaymentSummary = {
  authorizationRecordId: string;
  paymentStatus: string;
  authorizationDate?: string | null;
  inviteIds?: string[] | null;
};

export type MembershipPaymentsApiResponse = {
  data: { payments: MembershipPaymentSummary[] };
  meta?: { message?: string; details?: string | null };
};

export const getMembershipPayments = (): Promise<
  AxiosResponse<MembershipPaymentsApiResponse> | AxiosError
> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_REGISTRATION_API_URL}/membership/payments`
  };

  return doApiCall(request);
};

export const getPaymentLink = (): Promise<
  AxiosResponse<PaymentLinkApiResponse> | AxiosError
> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_REGISTRATION_API_URL}/membership/payment-link`
  };

  return doApiCall(request);
};

export type GiftingRecipient = {
  email: string;
  firstName: string;
  lastName: string;
};

export type InvitesPaymentLinkRequestBody = {
  recipients: GiftingRecipient[];
};

export type MembershipInvite = {
  firstName: string;
  lastName: string;
  email: string;
};

export type PostMembershipInvitesRequestBody = {
  invites: MembershipInvite[];
};

export type PostMembershipInvitesApiResponse = {
  data: { invites: MembershipInvite[] };
  meta?: { message?: string; details?: string | null };
};

export type MembershipInviteRecord = {
  inviteId: string;
  firstName: string;
  lastName: string;
  email: string;
  invitationDate: number;
  isPaid: boolean;
};

export type MembershipInvitesApiResponse = {
  data: { invites: MembershipInviteRecord[] };
  meta?: { message?: string; details?: string | null };
};

export const getMembershipInvites = (): Promise<
  AxiosResponse<MembershipInvitesApiResponse> | AxiosError
> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_REGISTRATION_API_URL}/membership/invites`
  };

  return doApiCall(request);
};

export const postMembershipInvites = (
  body: PostMembershipInvitesRequestBody
): Promise<AxiosResponse<PostMembershipInvitesApiResponse> | AxiosError> => {
  const request: AxiosRequestConfig = {
    data: body,
    method: "POST",
    url: `https://${REACT_APP_REGISTRATION_API_URL}/membership/invites`
  };

  return doApiCall(request);
};

export const postMembershipInvitesPaymentLink = (
  body: InvitesPaymentLinkRequestBody
): Promise<AxiosResponse<PaymentLinkApiResponse> | AxiosError> => {
  const request: AxiosRequestConfig = {
    data: body,
    method: "POST",
    url: `https://${REACT_APP_REGISTRATION_API_URL}/membership/invites/payment-link`
  };

  return doApiCall(request);
};

export type UserMfa = {
  mfaEnforcedOnLogin: boolean;
};

export type UserMfaResponse = {
  data: UserMfa;
  meta?: {
    message?: string;
    details?: string;
  };
};

export const getMemberMfaEnforcedStatus = () => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_REGISTRATION_API_URL}/user/mfa`
  };

  return doApiCall(request);
};

export const patchUserMfaEnforcedStatus = (status: boolean) => {
  const payload: UserMfa = { mfaEnforcedOnLogin: status };
  const request: AxiosRequestConfig = {
    method: "PATCH",
    url: `https://${REACT_APP_REGISTRATION_API_URL}/user/mfa`,
    data: payload
  };

  return doApiCall(request);
};

export const enableMfa = () => {
  return patchUserMfaEnforcedStatus(true);
};

export const disableMfa = () => {
  return patchUserMfaEnforcedStatus(false);
};
